import React from "react";
import { createPortal } from "react-dom";
import { Camera, ChevronDown, Check } from "lucide-react";
import { formatDateString, formatPhoneNumber } from "../../utils";
import {
  getMobileDeptDropdownTriggerClass,
  getMobileDeptOptionButtonClass,
  getMobileFaithStateButtonClass,
  MOBILE_DEPT_DROPDOWN_PANEL_CLASS,
  MOBILE_FAITH_STATE_OPTIONS,
  MOBILE_FORM_INPUT_BASE_CLASS,
  MOBILE_GENDER_TOGGLE_WRAPPER_CLASS,
  MOBILE_FORM_LABEL_CLASS,
  MOBILE_MODAL_HEADER_PRIMARY_BUTTON_CLASS,
  MOBILE_MODAL_HEADER_SECONDARY_BUTTON_CLASS,
  MOBILE_FORM_TEXTAREA_CLASS,
  getMobileGenderButtonClass,
} from "./mobileListUtils";

export default function MobileRegisterModal({
  isModalOpen,
  onClose,
  onTouchStart,
  onTouchMove,
  onModalSwipeEnd,
  isTeacher,
  formData,
  setFormData,
  teacherFormData,
  setTeacherFormData,
  handleImageUpload,
  handleSubmit,
  departmentOptions,
  isRegisterDeptOpen,
  setIsRegisterDeptOpen,
  icons,
}) {
  if (!isModalOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-white z-[9995] flex flex-col h-full overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={() => onModalSwipeEnd(() => onClose())}
    >
      <div className="relative flex justify-between items-center px-4 pt-[calc(env(safe-area-inset-top)+36px)] pb-3 border-b border-gray-100 bg-white/90 backdrop-blur-md sticky top-0 z-50 shrink-0 min-h-[50px]">
        <button onClick={onClose} className={MOBILE_MODAL_HEADER_SECONDARY_BUTTON_CLASS}>
          취소
        </button>
        <h2 className="font-bold text-lg text-gray-900">{isTeacher ? "교사 등록" : "학생 등록"}</h2>
        <button onClick={handleSubmit} className={MOBILE_MODAL_HEADER_PRIMARY_BUTTON_CLASS}>
          등록
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 pb-20 bg-[#F9F9F8]">
        <form onSubmit={handleSubmit} className="pb-24">
          <div className="relative group mb-6 flex justify-center pt-2">
            <div className="relative group">
              <div className="w-28 h-28 rounded-[2.5rem] bg-white border-4 border-white shadow-lg overflow-hidden flex items-center justify-center relative">
                {(isTeacher ? teacherFormData.photoUrl : formData.photoUrl) ? (
                  <img
                    src={isTeacher ? teacherFormData.photoUrl : formData.photoUrl}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img
                    src={
                      isTeacher
                        ? teacherFormData.gender === "여"
                          ? icons.teacher_female
                          : icons.teacher_male
                        : formData.gender === "남"
                          ? "/images/boy_animated_light.png"
                          : "/images/girl_animated_light.png"
                    }
                    alt=""
                    className="w-full h-full object-contain p-2"
                  />
                )}
                <label className="absolute inset-0 bg-black/0 hover:bg-black/10 transition cursor-pointer flex items-center justify-center group-hover:bg-black/20">
                  <input
                    id={`mobile-register-photo-${isTeacher ? "teacher" : "student"}`}
                    name={`mobile_register_photo_${isTeacher ? "teacher" : "student"}`}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <div className="w-8 h-8 rounded-full bg-white/90 shadow-sm flex items-center justify-center text-[#354F38] opacity-0 group-hover:opacity-100 transition-all scale-75 group-hover:scale-100">
                    <Camera size={16} />
                  </div>
                </label>
              </div>
              <div className="absolute -bottom-2 bg-[#354F38] text-white p-1.5 rounded-full shadow-md border-2 border-white pointer-events-none left-1/2 -translate-x-1/2">
                <Camera size={14} />
              </div>
            </div>
          </div>

          <div className="mb-4">
            <label className={MOBILE_FORM_LABEL_CLASS}>
              이름 <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                id={`mobile-register-name-${isTeacher ? "teacher" : "student"}`}
                name={`mobile_register_name_${isTeacher ? "teacher" : "student"}`}
                required
                className={["flex-1", MOBILE_FORM_INPUT_BASE_CLASS, "min-w-0"].join(" ")}
                value={isTeacher ? teacherFormData.name : formData.name}
                onChange={(e) =>
                  isTeacher
                    ? setTeacherFormData({ ...teacherFormData, name: e.target.value })
                    : setFormData({ ...formData, name: e.target.value })
                }
                placeholder="이름 입력"
              />
              <div className={MOBILE_GENDER_TOGGLE_WRAPPER_CLASS}>
                <button
                  type="button"
                  onClick={() =>
                    isTeacher
                      ? setTeacherFormData({ ...teacherFormData, gender: "남" })
                      : setFormData({ ...formData, gender: "남" })
                  }
                  className={getMobileGenderButtonClass(
                    isTeacher ? teacherFormData.gender : formData.gender,
                    "남"
                  )}
                >
                  남
                </button>
                <button
                  type="button"
                  onClick={() =>
                    isTeacher
                      ? setTeacherFormData({ ...teacherFormData, gender: "여" })
                      : setFormData({ ...formData, gender: "여" })
                  }
                  className={getMobileGenderButtonClass(
                    isTeacher ? teacherFormData.gender : formData.gender,
                    "여"
                  )}
                >
                  여
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className={MOBILE_FORM_LABEL_CLASS}>생년월일</label>
              <input
                id={`mobile-register-birth-${isTeacher ? "teacher" : "student"}`}
                name={`mobile_register_birth_${isTeacher ? "teacher" : "student"}`}
                className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
                placeholder="YY.MM.DD"
                value={isTeacher ? teacherFormData.birth : formData.birth}
                onChange={(e) =>
                  isTeacher
                    ? setTeacherFormData({
                        ...teacherFormData,
                        birth: formatDateString(e.target.value),
                      })
                    : setFormData({ ...formData, birth: formatDateString(e.target.value) })
                }
              />
            </div>
            <div>
              <label className={MOBILE_FORM_LABEL_CLASS}>연락처</label>
              <input
                id={`mobile-register-phone-${isTeacher ? "teacher" : "student"}`}
                name={`mobile_register_phone_${isTeacher ? "teacher" : "student"}`}
                className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
                placeholder="010-0000-0000"
                type="tel"
                value={isTeacher ? teacherFormData.phone : formData.phone}
                onChange={(e) =>
                  isTeacher
                    ? setTeacherFormData({
                        ...teacherFormData,
                        phone: formatPhoneNumber(e.target.value),
                      })
                    : setFormData({ ...formData, phone: formatPhoneNumber(e.target.value) })
                }
              />
            </div>
          </div>

          {!isTeacher && (
            <div className="mb-4">
              <label className={MOBILE_FORM_LABEL_CLASS}>보호자1</label>
              <div className="flex gap-2">
                <input
                  id="mobile-register-guardian1-name"
                  name="mobile_register_guardian1_name"
                  className={["w-20", MOBILE_FORM_INPUT_BASE_CLASS, "text-center"].join(" ")}
                  placeholder="관계"
                  value={formData.guardian1Name}
                  onChange={(e) => setFormData({ ...formData, guardian1Name: e.target.value })}
                />
                <input
                  id="mobile-register-guardian1-phone"
                  name="mobile_register_guardian1_phone"
                  className={["flex-1", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
                  placeholder="010-0000-0000"
                  type="tel"
                  value={formData.guardian1Phone}
                  onChange={(e) =>
                    setFormData({ ...formData, guardian1Phone: formatPhoneNumber(e.target.value) })
                  }
                />
              </div>
            </div>
          )}
          {!isTeacher && (
            <div className="mb-4">
              <label className={MOBILE_FORM_LABEL_CLASS}>
                보호자2 <span className="text-gray-300 font-normal">(선택)</span>
              </label>
              <div className="flex gap-2">
                <input
                  id="mobile-register-guardian2-name"
                  name="mobile_register_guardian2_name"
                  className={["w-20", MOBILE_FORM_INPUT_BASE_CLASS, "text-center"].join(" ")}
                  placeholder="관계"
                  value={formData.guardian2Name}
                  onChange={(e) => setFormData({ ...formData, guardian2Name: e.target.value })}
                />
                <input
                  id="mobile-register-guardian2-phone"
                  name="mobile_register_guardian2_phone"
                  className={["flex-1", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
                  placeholder="010-0000-0000"
                  type="tel"
                  value={formData.guardian2Phone}
                  onChange={(e) =>
                    setFormData({ ...formData, guardian2Phone: formatPhoneNumber(e.target.value) })
                  }
                />
              </div>
            </div>
          )}

          {!isTeacher && (
            <div className="mb-4">
              <label className={MOBILE_FORM_LABEL_CLASS}>학교/직장</label>
              <input
                id="mobile-register-school-student"
                name="mobile_register_school_student"
                className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
                value={formData.school}
                onChange={(e) => setFormData({ ...formData, school: e.target.value })}
                placeholder="학교 또는 직장명"
              />
            </div>
          )}
          {isTeacher && (
            <div className="mb-4">
              <label className={MOBILE_FORM_LABEL_CLASS}>학교/직장</label>
              <input
                id="mobile-register-school-teacher"
                name="mobile_register_school_teacher"
                className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
                value={teacherFormData.school || ""}
                onChange={(e) =>
                  setTeacherFormData({ ...teacherFormData, school: e.target.value })
                }
                placeholder="학교 또는 직장명"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className={MOBILE_FORM_LABEL_CLASS}>부서</label>
              <div className="space-y-2">
                <div className="space-y-2">
                  <div
                    onClick={() => setIsRegisterDeptOpen(!isRegisterDeptOpen)}
                    className={getMobileDeptDropdownTriggerClass(isRegisterDeptOpen)}
                  >
                    <span
                      className={
                        !(isTeacher ? teacherFormData.grade : formData.grade)
                          ? "text-gray-400"
                          : "text-gray-900"
                      }
                    >
                      {(isTeacher ? teacherFormData.grade : formData.grade) || "부서 선택"}
                    </span>
                    <ChevronDown
                      size={16}
                      className={`text-gray-400 transition-transform ${isRegisterDeptOpen ? "rotate-180" : ""}`}
                    />
                  </div>

                  {isRegisterDeptOpen && (
                    <div className={MOBILE_DEPT_DROPDOWN_PANEL_CLASS}>
                      {departmentOptions.map((dept) => (
                        <button
                          key={dept}
                          type="button"
                          onClick={() => {
                            const value = dept;
                            if (isTeacher) {
                              setTeacherFormData((prev) => ({ ...prev, grade: value }));
                            } else {
                              setFormData((prev) => ({ ...prev, grade: value }));
                            }
                            setIsRegisterDeptOpen(false);
                          }}
                          className={getMobileDeptOptionButtonClass(
                            isTeacher ? teacherFormData.grade : formData.grade,
                            dept
                          )}
                        >
                          {dept}
                          {(isTeacher ? teacherFormData.grade : formData.grade) === dept && (
                            <Check size={14} />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  {((isTeacher ? teacherFormData.grade : formData.grade) === "기타(직접 입력)" ||
                    ((isTeacher ? teacherFormData.grade : formData.grade) &&
                      !departmentOptions.some(
                        (opt) =>
                          opt === String(isTeacher ? teacherFormData.grade : formData.grade).trim()
                      ))) && (
                    <input
                      id={`mobile-register-grade-custom-${isTeacher ? "teacher" : "student"}`}
                      name={`mobile_register_grade_custom_${isTeacher ? "teacher" : "student"}`}
                      className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
                      placeholder="부서명 직접 입력"
                      value={
                        (isTeacher ? teacherFormData.grade : formData.grade) ===
                        "기타(직접 입력)"
                          ? ""
                          : isTeacher
                            ? teacherFormData.grade
                            : formData.grade
                      }
                      onChange={(e) =>
                        isTeacher
                          ? setTeacherFormData((prev) => ({ ...prev, grade: e.target.value }))
                          : setFormData((prev) => ({ ...prev, grade: e.target.value }))
                      }
                    />
                  )}
                </div>
              </div>
            </div>
            <div>
              <label className={MOBILE_FORM_LABEL_CLASS}>
                {isTeacher ? "담당" : "반 (선택)"}
              </label>
              <input
                id={`mobile-register-class-or-charge-${isTeacher ? "teacher" : "student"}`}
                name={`mobile_register_class_or_charge_${isTeacher ? "teacher" : "student"}`}
                className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
                placeholder={isTeacher ? "예: 찬양팀" : "예: 1반"}
                value={isTeacher ? teacherFormData.charge : formData.class}
                onChange={(e) =>
                  isTeacher
                    ? setTeacherFormData({ ...teacherFormData, charge: e.target.value })
                    : setFormData({ ...formData, class: e.target.value })
                }
              />
            </div>
          </div>

          {isTeacher && (
            <div className="mb-4">
              <label className={MOBILE_FORM_LABEL_CLASS}>직분</label>
              <input
                id="mobile-register-role-teacher"
                name="mobile_register_role_teacher"
                className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
                placeholder="예: 교사, 부장"
                value={teacherFormData.role}
                onChange={(e) => setTeacherFormData({ ...teacherFormData, role: e.target.value })}
              />
            </div>
          )}

          <div className="mb-4">
            <label className={MOBILE_FORM_LABEL_CLASS}>주소</label>
            <input
              id={`mobile-register-address-${isTeacher ? "teacher" : "student"}`}
              name={`mobile_register_address_${isTeacher ? "teacher" : "student"}`}
              className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
              value={isTeacher ? teacherFormData.address : formData.address}
              onChange={(e) =>
                isTeacher
                  ? setTeacherFormData({ ...teacherFormData, address: e.target.value })
                  : setFormData({ ...formData, address: e.target.value })
              }
              placeholder="주소를 입력하세요"
            />
          </div>

          {!isTeacher && (
            <div className="mb-4">
              <label className={MOBILE_FORM_LABEL_CLASS}>신앙상태</label>
              <div className="grid grid-cols-3 gap-2 w-full mt-2">
                {MOBILE_FAITH_STATE_OPTIONS.map((stateItem) => (
                  <button
                    type="button"
                    key={stateItem.label}
                    onClick={() => setFormData({ ...formData, faithState: stateItem.label })}
                    className={getMobileFaithStateButtonClass(
                      formData.faithState === stateItem.label,
                      stateItem
                    )}
                  >
                    {stateItem.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {isTeacher && (
            <div className="mb-4">
              <label className={MOBILE_FORM_LABEL_CLASS}>신앙상태</label>
              <div className="grid grid-cols-3 gap-2 w-full mt-2">
                {MOBILE_FAITH_STATE_OPTIONS.map((stateItem) => (
                  <button
                    type="button"
                    key={stateItem.label}
                    onClick={() =>
                      setTeacherFormData({ ...teacherFormData, faithState: stateItem.label })
                    }
                    className={getMobileFaithStateButtonClass(
                      (teacherFormData.faithState || "기존신자") === stateItem.label,
                      stateItem
                    )}
                  >
                    {stateItem.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className={MOBILE_FORM_LABEL_CLASS}>비고</label>
            <textarea
              id={`mobile-register-notes-${isTeacher ? "teacher" : "student"}`}
              name={`mobile_register_notes_${isTeacher ? "teacher" : "student"}`}
              className={["w-full", MOBILE_FORM_TEXTAREA_CLASS].join(" ")}
              value={isTeacher ? teacherFormData.notes : formData.notes}
              onChange={(e) =>
                isTeacher
                  ? setTeacherFormData({ ...teacherFormData, notes: e.target.value })
                  : setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="특이사항을 입력하세요"
            />
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
